import React, { useMemo, useState } from 'react';

import {
  BaseThreadFragmentFragmentGQLType,
  GetEventLogsQueryGQLType,
  ThreadActionGQLType,
  CreditFacilityEntityMetaFragmentFragmentGQLType,
  EventLogTypeGQLType,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { Icon } from '~/packages/common-frontend/components/icons';
import { EntityName as BaseEntityName } from '~/packages/event-logs/EntityName';
import {
  EventLogFilter,
  useEventLogFilters,
} from '~/packages/event-logs/EventLogFilters';
import { basicStringSearch } from '~/packages/event-logs/helpers';
import { EventLogExportDataRow } from '~/packages/event-logs/types';

// Constants
export const EVENT_LOG_TYPES: EventLogTypeGQLType[] = [];

// Types
export type EntityMeta = CreditFacilityEntityMetaFragmentFragmentGQLType;
type EventLogData = GetEventLogsQueryGQLType['eventLogs']['items'][number];
type ThreadData = BaseThreadFragmentFragmentGQLType;

// Exports
type EntityNameProps = Pick<EntityMeta, 'id' | 'name'>;
export const EntityName = ({
  id: creditFacilityId,
  name: creditFacilityName,
}: EntityNameProps) => {
  const { user } = useUserAuthOrThrow();
  return (
    <BaseEntityName
      label={creditFacilityName}
      tooltipLabel={`Credit Facility ID: ${creditFacilityId}`}
      isOnEntityRoute={false}
      to={
        user.userType === 'CAPITAL_PROVIDER'
          ? `/portfolio/summary?creditFacility=${creditFacilityId}`
          : undefined
      }
    />
  );
};

type ThreadFeedItemProps = {
  thread: ThreadData;
  onThreadAction?: (action: ThreadActionGQLType) => void;
  onMount?: (id: string) => void;
  onReady?: (id: string) => void;
};
export const ThreadFeedItem = ({ thread }: ThreadFeedItemProps) => {
  switch (thread.domain) {
    // !TODO Add domain => facility thread mapping here when facilities get comments
    // case 'creditFacility': {}
    default: {
      return null;
    }
  }
};

type EventFeedItemProps = {
  log: EventLogData;
  onThreadAction?: (action: ThreadActionGQLType) => void;
  onMount?: (id: string) => void;
  onReady?: (id: string) => void;
};
export const EventFeedItem = ({ log }: EventFeedItemProps) => {
  switch (log.__typename) {
    // !TODO Add log type => facility log item mapping here when facilities get event logs
    // case 'CreditFacility***': {}
    default: {
      return null;
    }
  }
};

export const EntityFilter = () => {
  const selectedIds = useEventLogFilters((s) => s.creditFacilityIds);
  const onSelect = useEventLogFilters((s) => s.setCreditFacilityIds);

  const [searchString, setSearchString] = useState<string>('');

  const { facilities } = useCreditFacilitiesOrThrow();
  const entities: EntityMeta[] = useMemo(
    () =>
      facilities.map((facility) => ({
        ...facility,
        __typename: 'EventLogCreditFacility',
      })),
    [facilities],
  );

  const options = useMemo(
    () =>
      entities
        .map((entity) => ({
          id: entity.id,
          textLabel: entity.name,
          data: entity,
        }))
        .filter((option) => basicStringSearch(searchString, option.textLabel)),
    [entities, searchString],
  );

  if (entities.length < 1) return null;

  return (
    <EventLogFilter
      onSearchInputChange={setSearchString}
      leftIcon={<Icon icon="bank" />}
      minWidth="10rem"
      noneLabel="Any Facility"
      singularLabel="Facility"
      pluralLabel="Facilities"
      selectedIds={selectedIds}
      options={options}
      onClose={onSelect}
    />
  );
};

/* eslint-disable camelcase */
export const toExportDataRow = (
  dataRow: EventLogExportDataRow,
  log: EventLogData,
): EventLogExportDataRow => {
  switch (log.__typename) {
    case 'DeliverableThreadCreateEvent':
    case 'FundingRequestCreatedEvent':
    case 'FundingRequestStatusChangeEvent':
    case 'FundingRequestThreadCreateEvent':
    case 'InvoiceThreadCreateEvent':
    case 'InvoiceCreatedEvent':
    case 'InvoiceUpdatedEvent': {
      return {
        ...dataRow,
        creditFacility_name: log.creditFacility.name,
        external_facility_id: log.creditFacility.externalFacilityId,
      };
    }

    case 'LimitedPartnerMappingUnmatchEvent':
    case 'LimitedPartnerCreatedEvent':
    case 'LimitedPartnerThreadCreateEvent':
    case 'LimitedPartnerUpdatedEvent':
    case 'ValleyBankLoanAccountThreadCreateEvent':
    case 'ThreadCreatedEvent':
    default: {
      return dataRow;
    }
  }
};
/* eslint-enable camelcase */
